/**
 * Plate Data Reverse Mapper Utility
 * Maps IDs back to labels for display purposes
 */

import { loadPlateData } from './plate-mapper';
import { AccidentReport, EnrichedAccidentReport } from '../types/accident-reports';

/**
 * Map color ID to label
 */
export async function mapColorIdToLabel(id: number): Promise<string> {
  try {
    const plateData = await loadPlateData();
    const color = plateData.colors.find((c) => c.id === id);
    return color?.label || 'Unknown';
  } catch (error) {
    console.error('Error mapping color ID to label:', error);
    return 'Unknown';
  }
}

/**
 * Map source ID to label (Arabic)
 */
export async function mapSourceIdToLabel(id: number): Promise<string> {
  try {
    const plateData = await loadPlateData();
    const source = plateData.sources.find((s) => s.id === id);
    return source?.label || 'غير معروف';
  } catch (error) {
    console.error('Error mapping source ID to label:', error);
    return 'غير معروف';
  }
}

/**
 * Map category ID to label (Arabic)
 */
export async function mapCategoryIdToLabel(id: number): Promise<string> {
  try {
    const plateData = await loadPlateData();
    const category = plateData.categories.find((c) => c.id === id);
    return category?.label || 'غير معروف';
  } catch (error) {
    console.error('Error mapping category ID to label:', error);
    return 'غير معروف';
  }
}

/**
 * Format date-time for display
 */
export function formatDateTime(isoDateTime: string, language: 'ar' | 'en' | 'ur' = 'ar'): string {
  try {
    const date = new Date(isoDateTime);
    
    if (isNaN(date.getTime())) {
      return isoDateTime; // Return original if invalid
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };

    // Use appropriate locale based on language
    const locale = language === 'ar' ? 'ar-AE' : language === 'ur' ? 'ur-PK' : 'en-US';
    
    return date.toLocaleString(locale, options);
  } catch (error) {
    console.error('Error formatting date:', error);
    return isoDateTime;
  }
}

/**
 * Enrich a single accident report with mapped labels
 */
export async function enrichReportWithLabels(
  report: AccidentReport,
  language: 'ar' | 'en' | 'ur' = 'ar'
): Promise<EnrichedAccidentReport> {
  const [colorLabel, sourceLabel, categoryLabel] = await Promise.all([
    mapColorIdToLabel(report.ItemRegistrationPlateColorId),
    mapSourceIdToLabel(report.PlateSourceId),
    mapCategoryIdToLabel(report.ItemRegistrationPlateCategoryId),
  ]);

  return {
    ...report,
    PlateColorLabel: colorLabel,
    PlateSourceLabel: sourceLabel,
    PlateCategoryLabel: categoryLabel,
    FormattedDateTime: formatDateTime(report.ReportDateTime, language),
  };
}

/**
 * Enrich multiple accident reports with mapped labels
 */
export async function enrichReportsWithLabels(
  reports: AccidentReport[],
  language: 'ar' | 'en' | 'ur' = 'ar'
): Promise<EnrichedAccidentReport[]> {
  return Promise.all(
    reports.map((report) => enrichReportWithLabels(report, language))
  );
}

/**
 * Get all mappings at once for efficient display
 */
export async function getAllMappings(): Promise<{
  colors: Map<number, string>;
  sources: Map<number, string>;
  categories: Map<number, string>;
}> {
  const plateData = await loadPlateData();

  const colors = new Map<number, string>();
  const sources = new Map<number, string>();
  const categories = new Map<number, string>();

  plateData.colors.forEach((c) => colors.set(c.id, c.label));
  plateData.sources.forEach((s) => sources.set(s.id, s.label));
  plateData.categories.forEach((c) => categories.set(c.id, c.label));

  return { colors, sources, categories };
}

